
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { useAuth } from "@/components/auth/AuthProvider";
import { Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export default function Index() {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [initializationComplete, setInitializationComplete] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');

  useEffect(() => {
    const init = async () => {
      try {
        console.log("Index component initializing - Auth state:", { user, loading });
        
        // Test Supabase connection
        const { error: pingError } = await supabase.from('profiles').select('count').limit(1);
        if (pingError) {
          console.error("Supabase connection test failed:", pingError);
          setConnectionStatus('error');
          toast({
            title: "Tilkoblingsfeil",
            description: "Kunne ikke koble til databasen. Prøv å laste siden på nytt.",
            variant: "destructive",
          });
          return;
        }
        
        setConnectionStatus('connected');
        console.log("Supabase connection test successful");

        if (!loading) {
          setInitializationComplete(true);
          if (user) {
            console.log("User authenticated:", user.id);
            toast({
              title: "Velkommen tilbake!",
              description: "Du er nå logget inn.",
            });
          }
        }
      } catch (error) {
        console.error("Initialization error:", error);
        setConnectionStatus('error');
        toast({
          title: "Feil ved oppstart",
          description: "Kunne ikke starte applikasjonen. Prøv å laste siden på nytt.",
          variant: "destructive",
        });
      }
    };

    init();
  }, [user, loading, toast]);

  // Vis tilkoblingsstatus mens vi venter
  if (connectionStatus === 'connecting' || loading || !initializationComplete) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-amber-50 to-white">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 text-amber-600 animate-spin mx-auto" />
          <p className="text-gray-600">
            {connectionStatus === 'connecting' 
              ? "Kobler til serveren..."
              : "Starter applikasjonen..."
            }
          </p>
        </div>
      </div>
    );
  }

  // Hvis vi har en tilkoblingsfeil, vis en feilmelding
  if (connectionStatus === 'error') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-amber-50 to-white">
        <div className="text-center space-y-4 max-w-md mx-auto p-6">
          <div className="text-red-600 text-xl font-semibold">
            Kunne ikke koble til serveren
          </div>
          <p className="text-gray-600">
            Det oppsto en feil ved tilkobling til serveren. 
            Vennligst prøv å laste siden på nytt, eller kontakt support hvis problemet vedvarer.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors"
          >
            Last siden på nytt
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log("No authenticated user found, redirecting to welcome page");
    return <Navigate to="/welcome" replace />;
  }

  console.log("User authenticated, showing dashboard");
  return (
    <DashboardLayout>
      <div className="page-container">
        <Navigate to="/dashboard" replace />
      </div>
    </DashboardLayout>
  );
}
